// src/parts/ContactAndReservationsPage.jsx
import React, { useState } from 'react';

const ContactAndReservationsPage = () => {
  // State for the reservation form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '1',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formMessage, setFormMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFormMessage(null);

    // Well-formatted message including reservation details
    const combinedMessage = `
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Date: ${formData.date}
Time: ${formData.time}
Guests: ${formData.guests}
${formData.message ? `Message: ${formData.message}` : ''}
    `.trim();

    const payload = {
      form_type: "corpflex-01",
      data: {
        email: formData.email,
        mailTo: "info@hfc.rw",
        subject: `HFC - New Reservation Request`,
        name: formData.name,
        phone: formData.phone,
        message: combinedMessage
      },
      test: false
    };

    try {
      const response = await fetch("https://kitech.rw/api/v1/mail", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setFormMessage({ type: 'success', text: 'Thank you! Your reservation request has been sent successfully.' });
        setFormData({
          name: '', email: '', phone: '', date: '', time: '', guests: '1', message: ''
        });
      } else {
        const errorData = await response.json();
        setFormMessage({ type: 'error', text: `An error occurred: ${errorData.message || 'Please try again.'}` });
      }
    } catch (error) {
      setFormMessage({ type: 'error', text: 'A network error occurred. Please check your connection and try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-extrabold text-center text-gray-900 mb-4">Contact Us & Make a Reservation</h1>
        <p className="text-center text-lg text-gray-600 mb-12">We'd love to hear from you! Reach out with any questions or book your table online.</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information Section */}
          <div className="bg-white rounded-lg shadow-xl overflow-hidden p-8">
            <h2 className="text-2xl font-bold text-yellow-600 mb-6">Get In Touch</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Address</h3>
                <p className="text-gray-600">
                  Location Gahanga next to MTN shop (KICUKIRO District)<br/>
                  Kigali, City, Rwanda
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Phone</h3>
                <p className="text-gray-600">
                  <a href="tel:+250787561177" className="hover:underline font-bold text-yellow-700">+250787561177</a><br/>
                  <a href="tel:+250732478400" className="hover:underline font-bold text-yellow-700">+250732478400</a>
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Email</h3>
                <p className="text-gray-600">
                  <a href="mailto:info@hfc.rw" className="hover:underline">info@hfc.rw</a>
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Opening Hours</h3>
                <p className="text-gray-600">Monday - Sunday<br/>10:00 AM - 10:00 PM</p>
              </div>
            </div>
            <div className="mt-8 rounded-lg overflow-hidden h-48">
              <iframe
                title="Honex Food Center Location"
                src="https://www.google.com/maps?q=Gahanga,+Kigali,+Rwanda&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              ></iframe>
            </div>
          </div>

          {/* Reservation Form Section */}
          <div className="bg-white p-8 rounded-lg shadow-xl">
            <h2 className="text-2xl font-bold text-yellow-600 mb-6">Make a Reservation</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Full Name" required className="p-3 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500" />
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" required className="p-3 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500" />
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" required className="p-3 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input type="date" name="date" value={formData.date} onChange={handleChange} required className="p-3 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500" />
                <input type="time" name="time" value={formData.time} onChange={handleChange} required className="p-3 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500" />
              </div>
              <div>
                <label htmlFor="guests" className="block text-sm font-medium text-gray-700 mb-1">Number of Guests</label>
                <input type="number" id="guests" name="guests" value={formData.guests} onChange={handleChange} min="1" required className="p-3 w-full border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500" />
              </div>
              <div>
                <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Special requests or additional information (optional)" rows="4" className="p-3 w-full border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500"></textarea>
              </div>
              <div>
                <button type="submit" disabled={isLoading} className="w-full bg-yellow-600 text-white font-bold py-3 px-4 rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:bg-gray-400 transition duration-300">
                  {isLoading ? 'Sending Request...' : 'Book Now'}
                </button>
              </div>
              {formMessage && (
                <div className={`p-4 rounded-md text-center ${formMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {formMessage.text}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactAndReservationsPage;
